import React, { useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select,
  TextField, Tooltip,
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import { editRoom } from "../../apis/RoomAPI";

const emptyForm = { room_id: "", name: "", lid: "", cap: "", credits_hourly: "" };
const isPositiveInteger = (value) =>
  value !== "" && Number.isInteger(Number(value)) && Number(value) > 0 &&
  Number(value) <= 2147483647;

export default function EditRoom({ data, locationsList, onRoomEdited }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const selected = form.room_id !== "";
  const validLocation = locationsList.some((location) => Number(location.lid) === Number(form.lid));
  const validCapacity = isPositiveInteger(form.cap);
  const validCredits = form.credits_hourly !== "" &&
    Number.isFinite(Number(form.credits_hourly)) && Number(form.credits_hourly) >= 0;
  const valid = selected && form.name.trim() !== "" && validLocation && validCapacity && validCredits;

  const close = () => {
    if (saving) return;
    setOpen(false);
    setForm(emptyForm);
    setError("");
  };

  const selectRoom = (event) => {
    const room = data.find((item) => Number(item.room_id) === Number(event.target.value));
    if (!room) return;
    setForm({
      room_id: Number(room.room_id), name: room.name,
      lid: Number(room.lid), cap: String(room.cap),
      credits_hourly: room.credits_hourly == null ? "" : String(room.credits_hourly),
    });
    setError("");
  };

  const change = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      await editRoom({
        room_id: Number(form.room_id), name: form.name.trim(),
        lid: Number(form.lid), cap: Number(form.cap),
        credits_hourly: Number(form.credits_hourly),
      });
    } catch (failure) {
      setError(failure.response?.data?.error || failure.message || "Failed to update room.");
      setSaving(false);
      return;
    }
    setSaving(false);
    setOpen(false);
    setForm(emptyForm);
    onRoomEdited();
  };

  return (
    <>
      <Tooltip title="Edit Room">
        <IconButton aria-label="Edit Room" onClick={() => setOpen(true)} size="small"
          sx={{ color: "black", float: "right", ml: 1, p: 0 }}>
          <EditNoteOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={close} fullWidth maxWidth="xs"
        PaperProps={{ component: "form", onSubmit: submit }}>
        <DialogTitle>Edit Room</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ pb: 3 }}>
            Select a room to edit, then modify the fields below.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <FormControl fullWidth>
            <InputLabel id="edit-room-selector-label">Select Room</InputLabel>
            <Select labelId="edit-room-selector-label" label="Select Room"
              value={form.room_id} onChange={selectRoom} disabled={saving} required>
              {data.map((room) => (
                <MenuItem key={room.room_id} value={Number(room.room_id)}>
                  {room.name} — {locationsList.find((location) => Number(location.lid) === Number(room.lid))?.name || "Unknown location"} (ID: {room.room_id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Room Name" name="name" value={form.name} onChange={change}
            disabled={!selected || saving} required fullWidth sx={{ mt: 3 }}
            error={selected && !form.name.trim()} helperText={selected && !form.name.trim() ? "Room name is required." : ""} />
          <FormControl fullWidth sx={{ mt: 3 }} disabled={!selected || saving} error={selected && !validLocation}>
            <InputLabel id="edit-room-location-label">Location</InputLabel>
            <Select labelId="edit-room-location-label" label="Location" name="lid"
              value={form.lid} onChange={change} required>
              {locationsList.map((location) => (
                <MenuItem key={location.lid} value={Number(location.lid)}>{location.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Capacity" name="cap" type="number" value={form.cap}
            onChange={change} disabled={!selected || saving} required fullWidth sx={{ mt: 3 }}
            inputProps={{ min: 1, max: 2147483647, step: 1 }} error={selected && !validCapacity}
            helperText={selected && !validCapacity ? "Enter a positive whole number." : ""} />
          <TextField label="Hourly Credits" name="credits_hourly" type="number" value={form.credits_hourly}
            onChange={change} disabled={!selected || saving} required fullWidth sx={{ mt: 3 }}
            inputProps={{ min: 0, step: "any" }} error={selected && !validCredits}
            helperText={selected && !validCredits ? "Enter zero or a positive number." : "Credits per hour; zero allows free bookings."} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: "20px" }}>
          <Button variant="outlined" onClick={close} disabled={saving}>Cancel</Button>
          <Button variant="contained" type="submit" disabled={!valid || saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
